import styled from 'styled-components'
import { useEffect, useState, Dispatch, SetStateAction } from 'react'
import { useTranslation } from 'react-i18next'
import SVG from 'react-inlinesvg'
import { useContextMotifGenerator } from '../../../contexts/motifGeneratorContext'
import { MotifGeneratorType } from '../types'

const ANIMATION_DURATION = 0.5

const Container = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: ${({ theme }) => theme.colors.black70};
  animation: simple-fade-in ${ANIMATION_DURATION}s forwards;
  z-index: 1001;

  &.hide {
    animation: simple-fade-out ${ANIMATION_DURATION}s forwards;
  }
`

const Modal = styled.div`
  font-size: 20px;
  position: absolute;
  top: 50%;
  left: 50%;
  border-radius: 70px;
  padding: 50px 70px 70px 70px;
  background-color: ${({ theme }) => theme.colors.white};
  animation: fade-in-y30-center ${ANIMATION_DURATION}s forwards;

  &.hide {
    animation: fade-out-y30-center ${ANIMATION_DURATION}s forwards;
  }

  p {
    text-align: center;
    margin-top: 0;
  }

  .generatorDownloadTitle {
    font-weight: bold;
    font-size: 30px;
  }

  .shapeText {
    display: flex;
    justify-content: center;
    align-items: center;
    margin-bottom: 10px;
    width: 81px;
    white-space: nowrap;
    overflow: visible;
  }

  .shapes {
    display: flex;
    justify-content: space-around;
    gap: 80px;

    div {
      padding: 0;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 10px;
    }

    button {
      position: relative;
      border: none;
      width: 81px;
      height: 81px;
      background: ${({ theme }) => theme.colors.red};

      svg {
        color: #fff;
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 70%;
        height: 70%;
      }
    }

    .tiles {
      button {
        background-image: linear-gradient(
            to bottom,
            ${({ theme }) => theme.colors.white} 100%,
            transparent 0
          ),
          linear-gradient(to bottom, ${({ theme }) => theme.colors.white} 100%, transparent 0),
          linear-gradient(to right, ${({ theme }) => theme.colors.white} 100%, transparent 0),
          linear-gradient(to right, ${({ theme }) => theme.colors.white} 100%, transparent 0);
        background-size: 100% 2px, 100% 2px, 2px 100%, 2px 100%;
        background-position: 0 33.3333%, 0 66.6666%, 33.3333% 0, 66.6666% 0;
        background-repeat: no-repeat;
      }
    }
    .tiles svg {
      filter: drop-shadow(3px 0 0 ${({ theme }) => theme.colors.red})
        drop-shadow(-3px 0 0 ${({ theme }) => theme.colors.red})
        drop-shadow(0 3px 0 ${({ theme }) => theme.colors.red})
        drop-shadow(0 -3px 0 ${({ theme }) => theme.colors.red});
    }
    .fragment{
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      button {
        transform: scale(0.9);
    }
    .bigTile {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      button {
        transform: scale(1.1);
      }
    }
    .bigTile svg {
      filter: drop-shadow(3px 0 0 ${({ theme }) => theme.colors.red})
        drop-shadow(-3px 0 0 ${({ theme }) => theme.colors.red})
        drop-shadow(0 3px 0 ${({ theme }) => theme.colors.red})
        drop-shadow(0 -3px 0 ${({ theme }) => theme.colors.red});
    }
  }
`

export enum ExportLayoutModalResponse {
  CANCEL = 'CANCEL',
  SINGLE = 'SINGLE',
  TILES = 'TILES',
}

type Props = {
  responseHandlerFn: (response: ExportLayoutModalResponse) => false | void
  setExportInvoked: Dispatch<SetStateAction<boolean>>
  setExportModal: Dispatch<SetStateAction<boolean>>
}

const ExportLayoutModal = ({ responseHandlerFn, setExportInvoked, setExportModal }: Props) => {
  const [shouldHide, setShouldHide] = useState(false)
  const { setLoading, setStartExport, selectedGeneratorType } = useContextMotifGenerator()
  const { t } = useTranslation()

  const isBigTileGenerator =
    selectedGeneratorType === MotifGeneratorType.PENROSE ||
    selectedGeneratorType === MotifGeneratorType.MAP

  useEffect(() => {
    if (!shouldHide) return
    setTimeout(() => {
      setExportInvoked(false)
      setExportModal(false)
    }, ANIMATION_DURATION * 1000)
  }, [shouldHide])

  const hide = async () => {
    setLoading(false)
    setShouldHide(true)
    setExportModal(false)
    setExportInvoked(false)
  }

  return (
    <Container className={shouldHide ? 'hide' : ''} onClick={() => hide()}>
      <Modal className={shouldHide ? 'hide' : ''} onClick={e => e.stopPropagation()}>
        <p className="generatorDownloadTitle">{t('generatorDownloadTitle')}</p>
        <div className="shapes">
          <div className={isBigTileGenerator ? 'fragment' : 'single'}>
            {isBigTileGenerator ? (
              <span className="shapeText">{t('generatorDownloadShapeFragment')}</span>
            ) : (
              <span className="shapeText">{t('generatorDownloadShapeSingle')}</span>
            )}
            <button
              type="button"
              onClick={() => {
                setStartExport(true)
                responseHandlerFn(ExportLayoutModalResponse.SINGLE)
                setShouldHide(true)
              }}
            >
              <SVG src="/icons/dotted_down_arrow.svg" className="svg-download" />
            </button>
          </div>
          {isBigTileGenerator ? (
            <div className="bigTile">
              <span className="shapeText">{t('generatorDownloadShapeBigTile')}</span>
              <button
                type="button"
                onClick={() => {
                  setShouldHide(true)
                  responseHandlerFn(ExportLayoutModalResponse.TILES)
                  setTimeout(() => {
                    setStartExport(true)
                  }, 50)
                }}
              >
                <SVG src="/icons/dotted_down_arrow.svg" className="svg-download" />
              </button>
            </div>
          ) : (
            <div className="tiles">
              <span className="shapeText">{t('generatorDownloadShapeTiles')}</span>
              <button
                type="button"
                onClick={() => {
                  setShouldHide(true)
                  responseHandlerFn(ExportLayoutModalResponse.TILES)
                  setTimeout(() => {
                    setStartExport(true)
                  }, 50)
                }}
              >
                <SVG src="/icons/dotted_down_arrow.svg" className="svg-download" />
              </button>
            </div>
          )}
        </div>
      </Modal>
    </Container>
  )
}

export default ExportLayoutModal
